import ActionButton from "@/components/buttons/ActionButton";
import { SortableItem } from "@/components/sortable/SortableItem";
import { isEmptyOrUndefined } from "@/utils/utils";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core/dist/types";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Card, Empty, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FieldConstraintViolation } from "../../../../../../common/types";
import { VehicleCalcDraft } from "../../../../drafts/types";
import { CalcType } from "../../../../enums";
import { CalcResultInstitutionsFilter } from "../../../components/calc-result/CalcResultInstitutionsFilter";
import { CalcResultSorting } from "../../../components/calc-result/CalcResultSorting";
import { CalcNavigation } from "../../../components/CalcNavigation";
import { DraftOfferForm } from "../../../components/DraftOfferForm";
import { CalcResultsSorting } from "../../../enums";
import { useInstitutionsFilter } from "../../../hooks";
import { OperationStage } from "../../../realty/enums";
import { CalcResult } from "../../../types";
import { moveActiveItemsFirst, sortCalcResultItems } from "../../../utils";
import { useVehicleCalcResults } from "../../contexts";
import type {
  VehicleCalc,
  VehicleCalcCrossSelling,
  VehicleCalcReinsurancesData,
  VehicleCalcResultData,
  VehicleFormClients,
} from "../../types";
import { VehicleSaveDraftButton } from "../VehicleSaveDraftButton";
import { VehicleCalcResult } from "./vehicle-calc-result/VehicleCalcResult";
import { VehicleCalcRecalculationForm } from "./VehicleCalcRecalculationForm";

type Props = {
  calcData: VehicleCalc;
  goToPreviousStep: () => void;
  handleGenerateContract: (selectedItem: CalcResult<VehicleCalcResultData>) => void;
  handleShowErrors: (errors: FieldConstraintViolation[]) => void;
  handleRecalculate: (reinsurancesData: VehicleCalcReinsurancesData, crossSelling: VehicleCalcCrossSelling) => void;
  clients: VehicleFormClients;
  draft?: VehicleCalcDraft;
};

export const VehicleCalcResultsView = ({
  calcData,
  goToPreviousStep,
  handleShowErrors,
  handleGenerateContract,
  handleRecalculate,
  clients,
  draft,
}: Props) => {
  const { t } = useTranslation();

  const { applicableResults: resultItems } = useVehicleCalcResults();

  const [draftOfferForm, setDraftOfferForm] = useState<"createOffer" | "createUpdateDraft" | "sendOffer">();
  const [isUpdateDraftOffer, setIsUpdateDraftOffer] = useState(false);

  const mainResultItems = useMemo(
    () => resultItems.filter((result) => result.calcType === calcData.type),
    [resultItems],
  );

  const gapResultItems = useMemo(() => resultItems.filter((result) => result.calcType === CalcType.GAP), [resultItems]);
  const pasResultItems = useMemo(() => resultItems.filter((result) => result.calcType === CalcType.PAS), [resultItems]);

  const [resultsSorting, setResultsSorting] = useState<CalcResultsSorting>(CalcResultsSorting.PRICE_ASC);
  const [calcResultItems, setCalcResultItems] = useState<CalcResult<VehicleCalcResultData>[]>(() =>
    sortCalcResultItems({
      sortBy: resultsSorting,
      items: mainResultItems,
    }),
  );

  const { excludedInstitutionIds, setExcludedInstitutionIds, visibleResultItems } =
    useInstitutionsFilter(calcResultItems);

  useEffect(() => {
    setCalcResultItems(
      sortCalcResultItems({
        sortBy: resultsSorting,
        items: mainResultItems,
      }),
    );
  }, [mainResultItems]);

  const handleSaveDraft = (isUpdate: boolean) => {
    setIsUpdateDraftOffer(isUpdate);
    setDraftOfferForm("createUpdateDraft");
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    const activeIndex = calcResultItems.findIndex((item) => item.id === active.id);
    const overIndex = calcResultItems.findIndex((item) => item.id === over.id);

    if (activeIndex !== overIndex) {
      setCalcResultItems((prev) => arrayMove(prev, activeIndex, overIndex));
    }
  };

  const handleItemActivation = (id: string, activation: boolean) => {
    setCalcResultItems((prev) => {
      const updatedItems = prev.map((item) => (item.id === id ? { ...item, active: activation } : item));

      return prev.some((item) => item.id === id && item.active !== activation)
        ? moveActiveItemsFirst(updatedItems)
        : updatedItems;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const getUnfinishedData = () => {
    const fieldPaths: string[] = [];

    if (calcData.vehicleData.newVehicle && isEmptyOrUndefined(calcData.vehicleData.vin)) {
      fieldPaths.push("vehicleData.vin");
    }

    return fieldPaths;
  };

  return (
    <div>
      <Card
        title={<h2>{t("calc.titles.insuranceOffer")}</h2>}
        className="card-box"
        extra={
          <Space>
            <VehicleSaveDraftButton draft={draft} handleSaveDraft={handleSaveDraft} />
            <ActionButton
              icon="download"
              color="green"
              variant="outlined"
              size="middle"
              disabled={!visibleResultItems.length}
              onClick={() => setDraftOfferForm("createOffer")}
            >
              {t("calc.actions.generateOffer")}
            </ActionButton>
            <ActionButton
              icon="mail"
              color="green"
              variant="outlined"
              size="middle"
              disabled={!visibleResultItems.length}
              onClick={() => setDraftOfferForm("sendOffer")}
            >
              {t("calc.actions.sendOffer")}
            </ActionButton>
          </Space>
        }
      >
        <CalcResultInstitutionsFilter
          results={mainResultItems}
          excludedInstitutionIds={excludedInstitutionIds}
          onChange={setExcludedInstitutionIds}
        />
      </Card>

      <VehicleCalcRecalculationForm calcData={calcData} onRecalculate={handleRecalculate} />

      <Card
        className="card-box"
        title={<h2 style={{ fontSize: 20 }}>{t("calc.vehicle.titles.calculation." + calcData.type)}</h2>}
        extra={
          <CalcResultSorting
            resultItems={mainResultItems}
            resultsSorting={resultsSorting}
            onChange={(sorting, results) => {
              setResultsSorting(sorting);
              setCalcResultItems(results);
            }}
          />
        }
      >
        {visibleResultItems.length ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={visibleResultItems} strategy={verticalListSortingStrategy}>
              {visibleResultItems.map((item) => (
                <SortableItem key={item.id} id={item.id} error={item.error} disabled={!item.active}>
                  <VehicleCalcResult
                    item={item}
                    holderClientType={calcData.clientsData.clients[calcData.clientsData.holderIndex]?.client.type}
                    handleItemActivation={handleItemActivation}
                    handleGenerateContract={handleGenerateContract}
                    handleShowErrors={handleShowErrors}
                    handleItemRecommendation={(itemId: string, activation: boolean) =>
                      setCalcResultItems((prev) =>
                        prev.map((item) => (item.id === itemId ? { ...item, recommended: activation } : item)),
                      )
                    }
                    unfinishedData={getUnfinishedData()}
                  />
                </SortableItem>
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <Empty
            description={
              mainResultItems.length ? t("calc.helpers.noDataByFilter") : t("calc.helpers.noResultsAvailable")
            }
          />
        )}
      </Card>

      {gapResultItems.length ? (
        <Card className="card-box" title={<h3 style={{ marginBottom: 0 }}>{t("calc.enums.calcType.GAP")}</h3>}>
          {gapResultItems.map((item) => (
            <VehicleCalcResult
              key={item.id}
              holderClientType={calcData.clientsData.clients[calcData.clientsData.holderIndex]?.client.type}
              item={item}
              handleItemActivation={handleItemActivation}
              handleGenerateContract={handleGenerateContract}
              handleShowErrors={handleShowErrors}
              hideDrag
            />
          ))}
        </Card>
      ) : undefined}

      {pasResultItems.length ? (
        <Card className="card-box" title={<h3 style={{ marginBottom: 0 }}>{t("calc.enums.calcType.PAS")}</h3>}>
          {pasResultItems.map((item) => (
            <VehicleCalcResult
              key={item.id}
              holderClientType={calcData.clientsData.clients[calcData.clientsData.holderIndex]?.client.type}
              item={item}
              handleItemActivation={handleItemActivation}
              handleGenerateContract={handleGenerateContract}
              handleShowErrors={handleShowErrors}
              hideDrag
            />
          ))}
        </Card>
      ) : undefined}

      <DraftOfferForm
        isUpdate={isUpdateDraftOffer}
        formType={draftOfferForm}
        calcData={calcData}
        calcResults={[...visibleResultItems, ...gapResultItems, ...pasResultItems].filter(
          (item) => item.active && !item.error,
        )}
        operationStage={OperationStage.CALCULATE}
        draft={draft}
        onFinish={() => setDraftOfferForm(undefined)}
        clients={clients}
      />

      <CalcNavigation onGoToPrevious={goToPreviousStep} />
    </div>
  );
};
